import apiClient from './client';

/**
 * Create a new activity session
 * @param {number} curriculumActivityId - ID of the curriculum activity
 */
export const createActivitySession = async (curriculumActivityId) => {
  const response = await apiClient.post(
    `/curriculum_activities/${curriculumActivityId}/activity_sessions`
  );
  return response.data;
};

/**
 * Get activity session details
 * @param {number} sessionId - ID of the activity session
 */
export const fetchActivitySession = async (sessionId) => {
  const response = await apiClient.get(`/activity_sessions/${sessionId}`);
  return response.data;
};

/**
 * Check if session snapshot is ready
 * @param {number} sessionId - ID of the activity session
 */
export const checkSnapshot = async (sessionId) => {
  try {
    const response = await apiClient.get(
      `/activity_sessions/${sessionId}/check_snapshot`
    );
    return response.data;
  } catch (error) {
    // 202 Accepted means not ready yet
    if (error.response?.status === 202) {
      return error.response.data;
    }
    throw error;
  }
};

/**
 * Complete an activity session
 * @param {number} sessionId - ID of the activity session
 * @param {object} finalSnapshot - Final snapshot data
 */
export const completeSession = async (sessionId, finalSnapshot) => {
  try {
    const response = await apiClient.post(
      `/activity_sessions/${sessionId}/complete`,
      { final_snapshot: finalSnapshot }
    );
    return response.data;
  } catch (error) {
    // 422 Unprocessable Entity means insufficient progress
    if (error.response?.status === 422) {
      return error.response.data;
    }
    throw error;
  }
};

/**
 * Save partial progress without completing
 * @param {number} sessionId - ID of the activity session
 * @param {object} snapshot - Progress snapshot data
 */
export const saveProgress = async (sessionId, snapshot) => {
  const response = await apiClient.post(
    `/activity_sessions/${sessionId}/save_progress`,
    { snapshot }
  );
  return response.data;
};

/**
 * Skip activity due to error
 * @param {number} sessionId - ID of the activity session
 */
export const skipActivity = async (sessionId) => {
  try {
    const response = await apiClient.post(
      `/activity_sessions/${sessionId}/skip`
    );
    return response.data;
  } catch (error) {
    // 422 Unprocessable Entity means session was cancelled or other error
    if (error.response?.status === 422) {
      return error.response.data;
    }
    throw error;
  }
};

/**
 * Mark external service as opened (e.g., kid clicked "Open Duolingo")
 * @param {number} sessionId - ID of the activity session
 */
export const markServiceOpened = async (sessionId) => {
  const response = await apiClient.post(
    `/activity_sessions/${sessionId}/mark_service_opened`
  );
  return response.data;
};
